/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DiffUIState } from './types';
import { BUTTON_ZOOM_SCALE, DiffUiDisplayType } from './constants';
import { ImageType } from '../../global/constants';
import { zoomToCenter, zoomToFitStage } from './utils';

export const getInitialState = (): DiffUIState => ({
  syncPan: true,
  offset: {
    x: 0,
    y: 0,
  },
  zoom: 1,
  stageDims: {
    height: 1,
    width: 1,
  },
  dims: {
    [ImageType.BASELINE]: {
      height: null,
      width: null,
    },
    [ImageType.SNAPSHOT]: {
      height: null,
      width: null,
    },
  },
  displayType: DiffUiDisplayType.SideBySide,
  diffSettings: {
    hideDiffs: false,
    emphasizeDiffs: false,
  },
  singleDisplayType: ImageType.SNAPSHOT,
});

const slice = createSlice({
  name: 'visual/filterBar',
  initialState: getInitialState(),
  reducers: {
    setOffset: (state, action: PayloadAction<DiffUIState['offset']>) => {
      state.offset = action.payload;
    },
    toggleDisplayType: (state) => {
      state.displayType = state.displayType === DiffUiDisplayType.Single ? (
        DiffUiDisplayType.SideBySide
      ) : DiffUiDisplayType.Single;
    },
    toggleSingleView: (state) => {
      state.singleDisplayType = state.singleDisplayType === ImageType.SNAPSHOT ? (
        ImageType.BASELINE
      ) : ImageType.SNAPSHOT;
    },
    toggleEmphasizeDiffs: (state) => {
      state.diffSettings.emphasizeDiffs = !state.diffSettings.emphasizeDiffs;
    },
    toggleHideDiffs: (state) => {
      state.diffSettings.hideDiffs = !state.diffSettings.hideDiffs;
    },
    // zoom / scale
    resetZoom: (state) => {
      zoomToCenter(state, 1);
    },
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },
    zoomIn: (state) => {
      zoomToCenter(state, state.zoom * BUTTON_ZOOM_SCALE);
    },
    zoomOut: (state) => {
      zoomToCenter(state, state.zoom / BUTTON_ZOOM_SCALE);
    },
    zoomToFit: (state, action: PayloadAction<boolean | undefined>) => {
      const { payload: enlarge } = action;
      zoomToFitStage(state, enlarge);
    },
    // pan
    togglePanSync: (state) => {
      state.syncPan = !state.syncPan;
    },
    // stage
    setStageDims: (state, action: PayloadAction<DiffUIState['stageDims']>) => {
      state.stageDims = action.payload;
      zoomToFitStage(state);
    },
    // images
    setImageDims: (state, action: PayloadAction<{
      type: ImageType,
      dims: {
        height: number | null,
        width: number | null,
      }
    }>) => {
      const { type, dims } = action.payload;
      state.dims[type] = dims;
    },
  },
});

export const { actions } = slice;

export default slice.reducer;
